import { API_BASE_URL } from '../config/constants';

/**
 * @param {Response} response
 * @param {string} fallback
 * @returns {Promise<string>}
 */
async function errorMessageFromResponse(response, fallback) {
  let errorData;
  try {
    errorData = await response.json();
  } catch {
    return fallback;
  }
  let errorMessage = errorData.error || fallback;
  if (
    errorData.details &&
    Array.isArray(errorData.details) &&
    errorData.details.length > 0
  ) {
    const validationErrors = errorData.details
      .map((detail) => detail.msg || detail.message)
      .filter((msg) => msg)
      .join(' ; ');
    if (validationErrors) {
      errorMessage = validationErrors;
    }
  }
  return errorMessage;
}

/**
 * Service pour gérer les applications Spotify
 */
class SpotifyAppService {
  /**
   * Crée une nouvelle application Spotify
   * @param {Object} appData - Données de l'application
   * @param {string} appData.appName - Nom de l'application
   * @param {string} appData.clientId - Client ID Spotify
   * @param {string} appData.clientSecret - Client Secret Spotify
   * @param {string} appData.redirectUri - URI de redirection
   * @returns {Promise<Object>} L'application créée
   */
  static async createApp(appData) {
    // Le token JWT est dans un cookie httpOnly
    // Il sera envoyé automatiquement avec credentials: 'include'
    const response = await fetch(`${API_BASE_URL}/spotify/apps`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(appData),
    });

    if (!response.ok) {
      const message = await errorMessageFromResponse(
        response,
        "Erreur lors de la création de l'application"
      );
      throw new Error(message);
    }

    const result = await response.json();
    return result.app;
  }

  /**
   * Récupère l'application Spotify de l'utilisateur connecté
   * @returns {Promise<Object|null>} L'application ou null si aucune
   */
  static async getUserApp() {
    // Le token JWT est dans un cookie httpOnly
    // Il sera envoyé automatiquement avec credentials: 'include'
    const response = await fetch(`${API_BASE_URL}/spotify/apps`, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error("Erreur lors de la récupération de l'application");
    }

    const data = await response.json();
    return data.hasApp ? data.app : null;
  }

  /**
   * Met à jour l'application Spotify
   * @param {Object} appData - Données mises à jour
   * @returns {Promise<Object>} L'application mise à jour
   */
  static async updateApp(appData) {
    // Le token JWT est dans un cookie httpOnly
    // Il sera envoyé automatiquement avec credentials: 'include'
    const response = await fetch(`${API_BASE_URL}/spotify/apps`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(appData),
    });

    if (!response.ok) {
      const message = await errorMessageFromResponse(
        response,
        "Erreur lors de la mise à jour de l'application"
      );
      throw new Error(message);
    }

    const result = await response.json();
    return result.app;
  }

  /**
   * Récupère l'URI de redirection suggérée basée sur l'environnement actuel
   * @returns {Promise<string>} L'URI de redirection suggérée
   */
  static async getSuggestedRedirectUri() {
    const response = await fetch(
      `${API_BASE_URL}/spotify/suggested-redirect-uri`,
      {
        credentials: 'include',
      }
    );

    if (!response.ok) {
      throw new Error("Erreur lors de la récupération de l'URI suggérée");
    }

    const data = await response.json();
    return data.suggestedRedirectUri;
  }

  /**
   * Supprime l'application Spotify
   * @returns {Promise<void>}
   */
  static async deleteApp() {
    // Le token JWT est dans un cookie httpOnly
    // Il sera envoyé automatiquement avec credentials: 'include'
    const response = await fetch(`${API_BASE_URL}/spotify/apps`, {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const message = await errorMessageFromResponse(
        response,
        "Erreur lors de la suppression de l'application"
      );
      throw new Error(message);
    }
  }

  /**
   * Active/désactive l'application Spotify
   * @param {boolean} isActive - État souhaité
   * @returns {Promise<Object>} L'application mise à jour
   */
  static async toggleApp(isActive) {
    // Le token JWT est dans un cookie httpOnly
    // Il sera envoyé automatiquement avec credentials: 'include'
    const response = await fetch(`${API_BASE_URL}/spotify/apps/toggle`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ isActive }),
    });

    if (!response.ok) {
      const message = await errorMessageFromResponse(
        response,
        'Erreur lors du changement de statut'
      );
      throw new Error(message);
    }

    const result = await response.json();
    return result.app;
  }
}

export default SpotifyAppService;
